import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { useTheme } from "@/lib/theme";
import { Moon, Sun } from "lucide-react";
import Logo from "@/components/Logo";

export default function Settings() {
  const [form, setForm] = useState(null);
  const { theme, toggle } = useTheme();

  useEffect(() => { endpoints.settings().then(r => setForm(r.data)); }, []);

  const save = async () => {
    await endpoints.updateSettings(form);
    toast.success("Settings saved");
  };

  if (!form) return <Layout title="Settings"><div className="text-muted-foreground">Loading...</div></Layout>;

  return (
    <Layout title="Settings" subtitle="Business configuration">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 rounded-xl lg:col-span-2">
          <div className="flex items-center gap-3 mb-6">
            <Logo size={48}/>
            <div>
              <div className="font-display font-bold text-lg">Business Information</div>
              <div className="text-xs text-muted-foreground">Shown on invoices, estimates, and emails</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5 col-span-2"><Label>Business Name</Label><Input value={form.name||""} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="settings-name"/></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone||""} onChange={(e)=>setForm({...form, phone:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.email||""} onChange={(e)=>setForm({...form, email:e.target.value})}/></div>
            <div className="space-y-1.5 col-span-2"><Label>Address</Label><Input value={form.address||""} onChange={(e)=>setForm({...form, address:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Tax Rate (%)</Label><Input type="number" step="0.01" value={form.tax_rate||0} onChange={(e)=>setForm({...form, tax_rate:Number(e.target.value)})}/></div>
            <div className="space-y-1.5"><Label>Logo Initials</Label><Input value={form.logo_text||""} onChange={(e)=>setForm({...form, logo_text:e.target.value})}/></div>
            <div className="space-y-1.5 col-span-2"><Label>Service Areas (comma separated)</Label>
              <Textarea rows={2} value={(form.service_areas||[]).join(", ")} onChange={(e)=>setForm({...form, service_areas:e.target.value.split(",").map(s=>s.trim()).filter(Boolean)})}/>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <Button onClick={save} data-testid="settings-save">Save Settings</Button>
          </div>
        </Card>

        <Card className="p-6 rounded-xl space-y-4">
          <div className="font-display font-bold text-lg">Appearance</div>
          <div className="flex items-center justify-between border border-border rounded-lg p-4">
            <div>
              <div className="font-semibold text-sm">Theme</div>
              <div className="text-xs text-muted-foreground capitalize">{theme} mode</div>
            </div>
            <Button variant="outline" size="icon" onClick={toggle} data-testid="settings-theme-toggle">
              {theme === "dark" ? <Sun className="size-4"/> : <Moon className="size-4"/>}
            </Button>
          </div>
          <div className="space-y-2">
            <div className="font-semibold text-sm">Notifications</div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" defaultChecked /> Email invoices automatically</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" defaultChecked /> Notify crew of new jobs</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" /> SMS reminders to customers</label>
          </div>
          <div className="pt-4 border-t border-border">
            <div className="font-semibold text-sm mb-1">Roles & Permissions</div>
            <div className="text-xs text-muted-foreground">Owner has full access. Add employees via the Employees module.</div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
